/**
 * Comments on a rendered Mermaid diagram (TASK-045).
 *
 * A diagram's text IS its Mermaid source: it is the code block's text, the
 * text `resolveAnchor`, `highlightRanges`, the edit guard and follow anchors
 * already work on. So nothing here changes what is stored. This file only
 * links the source to the labels mermaid drew from it, in both directions:
 * a label selected in the SVG to a slice of the source (lib/anchors.ts,
 * `anchorForDiagram`), and a stored range back to the labels to tint
 * (MermaidBlock.svelte).
 *
 * NO MERMAID PARSING. The link is a *diagram key*: the source with every
 * `<br>` variant and all whitespace removed, with an index map back to raw
 * offsets (the `normalizeWithMap` pattern). A label's key is its
 * `textContent` with all whitespace removed -- mermaid draws `<br/>` as a
 * `<br>` element, which contributes nothing to `textContent`, so
 * `The birches<br/>where they met` and the label it draws have the same key.
 * A label whose key occurs exactly once in the diagram's key is that slice of
 * the source; anything else (a label written twice, `#quot;`, `<b>`, markdown
 * strings) is not matched and falls back to the whole diagram. Wrong in the
 * safe direction: a comment is never pinned to a label it was not made on.
 *
 * Label text is written by whoever wrote the diagram. It is only ever
 * compared as a string here: never put in a selector, never into HTML.
 */

/** Mermaid's HTML labels: nodes, edges and subgraph titles. `.edgeLabel`
    alone would also match the `g.edgeLabel` wrapper around an edge's label. */
export const LABEL_SELECTOR = 'foreignObject span.nodeLabel, foreignObject span.edgeLabel'

/** Every line break mermaid accepts in a label (its own `lineBreakRegex`). */
const BR = /<br\s*\/?>/iy
const WS = /\s/

export interface DiagramKey {
  /** The source with `<br>` variants and whitespace removed. */
  key: string
  /** `at[i]` is the raw offset of key character `i`. */
  at: number[]
}

/** The diagram key of a Mermaid source, with its map back to raw offsets. */
export function diagramKey(src: string): DiagramKey {
  let key = ''
  const at: number[] = []
  let i = 0
  while (i < src.length) {
    BR.lastIndex = i
    const br = BR.exec(src)
    if (br) {
      i += br[0].length
      continue
    }
    if (!WS.test(src[i])) {
      key += src[i]
      at.push(i)
    }
    i++
  }
  return { key, at }
}

/** A drawn label's key: its `textContent`, whitespace removed. */
export function labelKey(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, '')
}

/** A drawn label as a person reads it: `innerText`, whitespace runs as one space. */
export function labelDisplay(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim()
}

/**
 * The raw slice `[from, to)` of `src` whose key is the ONE occurrence of
 * `key` in the diagram's key; null when it occurs zero times or more than
 * once (overlapping, as the server counts), or when `key` is empty.
 */
export function sliceForKey(src: string, key: string): { from: number; to: number } | null {
  if (!key) return null
  const dk = diagramKey(src)
  const first = dk.key.indexOf(key)
  if (first === -1 || dk.key.indexOf(key, first + 1) !== -1) return null
  return { from: dk.at[first], to: dk.at[first + key.length - 1] + 1 }
}

/**
 * What a person is shown for an anchor's words on a diagram: the label as it
 * reads (`<br>` variants as a space), or null for "the whole diagram" -- the
 * whole source, or anything spanning lines, which is Mermaid and not a label.
 * `src` is the diagram's current source when the page has it.
 */
export function diagramShown(text: string | null | undefined, src?: string): string | null {
  const t = (text ?? '').trim()
  if (!t || /[\r\n]/.test(t) || (src !== undefined && t === src.trim())) return null
  return labelDisplay(t.replace(/<br\s*\/?>/gi, ' ')) || null
}
